import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Button, Dropdown, Input, Select, Space, Tag, message} from "antd";
import {
    CheckCircleFilled,
    CheckOutlined,
    ClearOutlined,
    DownloadOutlined,
    DownOutlined,
    EditOutlined,
    FullscreenOutlined,
    LeftOutlined,
    PictureOutlined,
    ReloadOutlined,
    RightOutlined,
    UploadOutlined,
    ZoomInOutlined,
    ZoomOutOutlined
} from "@ant-design/icons";
import {
    adminApproveRetouchPhoto,
    adminDeleteRetouchComment,
    adminPostRetouchComment,
    approveRetouchPhoto,
    fetchRetouchComments,
    postRetouchComment
} from "../../utils/retouchReviewApi";
import {downloadRetouchReviewPhoto} from "../../utils/retouchReviewDownload";
import {formatRetouchVersion, getRetouchReviewCopy} from "../../utils/retouchReviewCopy";
import {CANCELLED_MESSAGE, UNSUPPORTED_MESSAGE, downloadAllRetouchPhotosAsZip} from "../../utils/retouchReviewDownloadAll";
import {useTranslation} from "../../i18n";
import RetouchBatchSavePanel from "./RetouchBatchSavePanel";
import "./RetouchCompare.css";

function transformStyle(scale, pan) {
    return {transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`};
}

function isRetouchMobileDevice() {
    if (typeof window === "undefined") return false;
    return /Android.*Mobile|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        || window.matchMedia?.("(max-width: 760px)").matches
        || window.innerWidth <= 760;
}

function ZoomControls({scale, copy, onZoomIn, onZoomOut}) {
    return <>
        {scale > 1 && <button type="button" className="retouch-compare__zoom-button retouch-compare__zoom-button--out"
                              title={copy.zoomOut} aria-label={copy.zoomOut} onClick={onZoomOut}><ZoomOutOutlined/>
        </button>}
        <button type="button" className="retouch-compare__zoom-button retouch-compare__zoom-button--in"
                title={copy.zoomIn} aria-label={copy.zoomIn} onClick={onZoomIn} disabled={scale >= 8}><ZoomInOutlined/>
        </button>
    </>;
}

function buildRetouchThumbnailUrl(originalUrl) {
    if (!originalUrl) return "";
    try {
        const url = new URL(originalUrl);
        return `${url.origin}/cdn-cgi/image/width=400,format=auto,quality=85${url.pathname}${url.search}`;
    } catch {
        return "";
    }
}

function RetouchPhotoGallery({photos, selectedId, copy, onSelect}) {
    const [failedThumbnails, setFailedThumbnails] = useState(() => new Set());
    const stripRef = useRef(null);
    const itemRefs = useRef(new Map());

    useEffect(() => {
        const strip = stripRef.current;
        const item = itemRefs.current.get(selectedId);
        if (!strip || !item) return;
        const stripRect = strip.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        strip.scrollBy({
            left: itemRect.left - stripRect.left - (strip.clientWidth - item.clientWidth) / 2,
            behavior: "smooth"
        });
    }, [selectedId]);

    const setItemRef = useCallback((photoId, node) => {
        if (node) itemRefs.current.set(photoId, node);
        else itemRefs.current.delete(photoId);
    }, []);

    const markThumbnailFailed = useCallback((photoId) => {
        setFailedThumbnails((previous) => {
            if (previous.has(photoId)) return previous;
            const next = new Set(previous);
            next.add(photoId);
            return next;
        });
    }, []);

    return (
        <div ref={stripRef} className="retouch-compare__gallery-strip" aria-label={copy.gallery}>
            {photos.map((item) => {
                const thumbnailUrl = buildRetouchThumbnailUrl(item.original?.url);
                const showFallback = !thumbnailUrl || failedThumbnails.has(item.id);
                const selected = item.id === selectedId;
                return (
                    <button
                        type="button"
                        key={item.id}
                        ref={(node) => setItemRef(item.id, node)}
                        className={`retouch-compare__gallery-item${selected ? " retouch-compare__gallery-item--selected" : ""}`}
                        aria-current={selected ? "true" : undefined}
                        aria-label={item.relativePath}
                        title={item.relativePath}
                        onClick={() => onSelect(item)}
                    >
                        {showFallback
                            ? <span className="retouch-compare__gallery-fallback">{item.relativePath}</span>
                            : <img src={thumbnailUrl} alt={item.relativePath}
                                   onError={() => markThumbnailFailed(item.id)}/>}
                    </button>
                );
            })}
        </div>
    );
}

function RetouchCompare({
                            photo,
                            token,
                            adminMode = false,
                            role = "customer",
                            authorName = "",
                            albumName = "",
                            onPhotoChanged,
                            photos = [],
                            onNavigate,
                            onUploadPhoto,
                            onUploadBatch
                        }) {
    const {locale} = useTranslation();
    const copy = getRetouchReviewCopy(locale);
    const [version, setVersion] = useState(photo.latestVersion || Object.keys(photo.versions || {})[0] || "");
    const [comments, setComments] = useState(photo.comments || []);
    const [loadingComments, setLoadingComments] = useState(!adminMode);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState("");
    const [strokes, setStrokes] = useState([]);
    const [scale, setScale] = useState(1);
    const [pan, setPan] = useState({x: 0, y: 0});
    const [drawing, setDrawing] = useState(false);
    const [annotationTarget, setAnnotationTarget] = useState("retouch");
    const [downloading, setDownloading] = useState(false);
    const [zipDownloading, setZipDownloading] = useState(false);
    const [batchSaveOpen, setBatchSaveOpen] = useState(false);
    const [batchSavePhotos, setBatchSavePhotos] = useState([]);
    const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
    const [photoPreviewOriginal, setPhotoPreviewOriginal] = useState(false);
    const [mobileOriginal, setMobileOriginal] = useState(false);
    const [mobileSaveOpen, setMobileSaveOpen] = useState(false);
    const [mobileGalleryOpen, setMobileGalleryOpen] = useState(false);
    const [zoomHintTarget, setZoomHintTarget] = useState("");
    const galleryShellRef = useRef(null);
    const gestureScaleRef = useRef(1);
    const zoomHintTimerRef = useRef(null);
    const [imageState, setImageState] = useState({photoId: "", version: "", originalUrl: null, versionUrl: null});

    const initialComments = useMemo(() => photo.comments || [], [photo.comments]);
    const defaultVersion = photo.latestVersion || Object.keys(photo.versions || {})[0] || "";
    const originalUrl = photo.original?.url || null;
    const defaultVersionUrl = photo.versions?.[defaultVersion]?.url || null;
    const versionUrl = photo.versions?.[version]?.url || null;
    const versionOptions = useMemo(() => Object.keys(photo.versions || {}).map((item) => ({
        value: item,
        label: formatRetouchVersion(item, copy)
    })), [copy, photo.versions]);
    const noRetouchVersion = versionOptions.length === 0;
    const originalComments = useMemo(() => comments.filter((comment) => comment.version === "original"), [comments]);
    const versionComments = useMemo(() => comments.filter((comment) => comment.version && comment.version !== "original" && comment.version === version), [comments, version]);

    useEffect(() => {
        let active = true;
        setVersion(defaultVersion);
        setComments(initialComments);
        setStrokes([]);
        setText("");
        setScale(1);
        setPan({x: 0, y: 0});
        setEditing(false);
        setDrawing(false);
        setAnnotationTarget("retouch");
        setPhotoPreviewOriginal(false);
        setMobileOriginal(false);
        setMobileSaveOpen(false);
        setMobileGalleryOpen(false);
        if (!adminMode && token) {
            setLoadingComments(true);
            fetchRetouchComments(token, photo.id).then((nextComments) => {
                if (active) setComments(nextComments);
            }).catch(() => {
            }).finally(() => {
                if (active) setLoadingComments(false);
            });
        } else setLoadingComments(false);
        return () => {
            active = false;
        };
    }, [adminMode, defaultVersion, initialComments, photo.id, token]);

    useEffect(() => {
        setImageState({photoId: photo.id, version: defaultVersion, originalUrl: null, versionUrl: null});
        const frame = window.requestAnimationFrame(() => {
            setImageState({photoId: photo.id, version: defaultVersion, originalUrl, versionUrl: defaultVersionUrl});
        });
        return () => window.cancelAnimationFrame(frame);
    }, [defaultVersion, defaultVersionUrl, originalUrl, photo.id]);

    useEffect(() => {
        setScale(1);
        setPan({x: 0, y: 0});
        setStrokes([]);
        setDrawing(false);
        setImageState((previous) => ({...previous, version: "", originalUrl: null, versionUrl: null}));
        const frame = window.requestAnimationFrame(() => {
            setImageState({photoId: photo.id, version, originalUrl, versionUrl});
        });
        return () => window.cancelAnimationFrame(frame);
    }, [originalUrl, photo.id, version, versionUrl]);

    useEffect(() => {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (!viewport) return undefined;
        const isMobile = window.matchMedia?.("(max-width: 760px)").matches || window.innerWidth <= 760;
        // Keep the document viewport unchanged on mobile. Rewriting the viewport
        // meta tag during mount can make mobile browsers recalculate their visual
        // viewport and open this page slightly zoomed in. The base document meta
        // tag already uses initial-scale=1 and still permits native page zoom.
        if (isMobile) return undefined;
        const previous = viewport.getAttribute("content") || "width=device-width, initial-scale=1";
        viewport.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no");
        return () => viewport.setAttribute("content", previous);
    }, []);

    useEffect(() => {
        const isDesktop = () => !(window.matchMedia?.("(max-width: 760px)").matches || window.innerWidth <= 760);
        const preventDesktopBrowserZoom = (event) => {
            if (isDesktop() && (event.ctrlKey || event.metaKey)) event.preventDefault();
        };
        const preventDesktopGestureZoom = (event) => {
            if (isDesktop()) event.preventDefault();
        };
        const preventDesktopZoomKeys = (event) => {
            if (isDesktop() && (event.ctrlKey || event.metaKey) && ["+", "-", "=", "0"].includes(event.key)) event.preventDefault();
        };
        window.addEventListener("wheel", preventDesktopBrowserZoom, {passive: false});
        window.addEventListener("gesturestart", preventDesktopGestureZoom, {passive: false});
        window.addEventListener("gesturechange", preventDesktopGestureZoom, {passive: false});
        window.addEventListener("gestureend", preventDesktopGestureZoom, {passive: false});
        window.addEventListener("keydown", preventDesktopZoomKeys, {passive: false});
        return () => {
            window.removeEventListener("wheel", preventDesktopBrowserZoom);
            window.removeEventListener("gesturestart", preventDesktopGestureZoom);
            window.removeEventListener("gesturechange", preventDesktopGestureZoom);
            window.removeEventListener("gestureend", preventDesktopGestureZoom);
            window.removeEventListener("keydown", preventDesktopZoomKeys);
        };
    }, []);

    useEffect(() => {
        if (!photoPreviewOpen) return undefined;
        const closeOnEscape = (event) => {
            if (event.key === "Escape") setPhotoPreviewOpen(false);
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", closeOnEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", closeOnEscape);
        };
    }, [photoPreviewOpen]);

    useEffect(() => {
        if (!mobileSaveOpen) return undefined;
        const closeOnEscape = (event) => {
            if (event.key === "Escape") setMobileSaveOpen(false);
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", closeOnEscape);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", closeOnEscape);
        };
    }, [mobileSaveOpen]);

    useEffect(() => () => {
        if (zoomHintTimerRef.current) window.clearTimeout(zoomHintTimerRef.current);
    }, []);

    const pointFromEvent = useCallback((event) => {
        const rect = event.currentTarget?.getBoundingClientRect();
        if (!rect) return null;
        const x = Math.max(0, Math.min(1, (event.clientX - rect.left - rect.width / 2 - pan.x) / (rect.width * scale) + 0.5));
        const y = Math.max(0, Math.min(1, (event.clientY - rect.top - rect.height / 2 - pan.y) / (rect.height * scale) + 0.5));
        return {x, y};
    }, [pan, scale]);

    const handlePointerDown = useCallback((event, target) => {
        if (!editing) return;
        const point = pointFromEvent(event);
        if (!point) return;
        if (target !== annotationTarget) {
            setStrokes([]);
            setAnnotationTarget(target);
        }
        setDrawing(true);
        setStrokes((prev) => [...prev, [point]]);
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }, [annotationTarget, editing, pointFromEvent]);

    const handlePointerMove = useCallback((event) => {
        if (!drawing) return;
        const point = pointFromEvent(event);
        if (!point) return;
        setStrokes((prev) => prev.map((stroke, index) => index === prev.length - 1 ? [...stroke, point] : stroke));
    }, [drawing, pointFromEvent]);

    const handlePointerUp = useCallback(() => setDrawing(false), []);

    const showZoomHint = useCallback((target) => {
        setZoomHintTarget(target);
        if (zoomHintTimerRef.current) window.clearTimeout(zoomHintTimerRef.current);
        zoomHintTimerRef.current = window.setTimeout(() => {
            setZoomHintTarget("");
            zoomHintTimerRef.current = null;
        }, 2200);
    }, []);

    const hideZoomHint = useCallback(() => {
        setZoomHintTarget("");
        if (zoomHintTimerRef.current) {
            window.clearTimeout(zoomHintTimerRef.current);
            zoomHintTimerRef.current = null;
        }
    }, []);

    const handleWheel = useCallback((event, target) => {
        // Follow the interaction used by embedded maps: ordinary wheel/two-finger
        // scrolling belongs to the page; only a modified wheel zooms the photo.
        if (!event.ctrlKey && !event.metaKey) {
            showZoomHint(target);
            return;
        }
        hideZoomHint();
        event.preventDefault();
        setScale((value) => Math.max(1, Math.min(8, value * (event.deltaY > 0 ? 0.9 : 1.1))));
    }, [hideZoomHint, showZoomHint]);

    const handleGestureStart = useCallback((event) => {
        if (window.matchMedia?.("(max-width: 760px)").matches || window.innerWidth <= 760) return;
        event.preventDefault();
        gestureScaleRef.current = scale;
    }, [scale]);

    const handleGestureChange = useCallback((event) => {
        if (window.matchMedia?.("(max-width: 760px)").matches || window.innerWidth <= 760) return;
        event.preventDefault();
        if (typeof event.scale === "number") setScale(Math.max(1, Math.min(8, gestureScaleRef.current * event.scale)));
    }, []);

    const handleGestureEnd = useCallback((event) => {
        if (window.matchMedia?.("(max-width: 760px)").matches || window.innerWidth <= 760) return;
        event.preventDefault();
    }, []);

    const handlePanPointerMove = useCallback((event) => {
        // Do not consume touch pointers: mobile browsers need them for native pinch/page zoom.
        if (editing || event.pointerType === "touch") return;
        if (event.buttons !== 1) return;
        setPan((value) => ({x: value.x + (event.movementX || 0), y: value.y + (event.movementY || 0)}));
    }, [editing]);

    const openPhotoPreview = useCallback(() => {
        setPhotoPreviewOriginal(false);
        setPhotoPreviewOpen(true);
    }, []);

    const closePhotoPreview = useCallback(() => {
        setPhotoPreviewOpen(false);
        setPhotoPreviewOriginal(false);
    }, []);

    const zoomIn = useCallback(() => {
        setScale((value) => Math.min(8, value * 1.25));
    }, []);

    const zoomOut = useCallback(() => {
        const nextScale = Math.max(1, scale / 1.25);
        setScale(nextScale);
        if (nextScale === 1) setPan({x: 0, y: 0});
    }, [scale]);

    const toggleEditing = useCallback(() => {
        setEditing((value) => {
            if (!value) message.info(copy.drawingHint);
            return !value;
        });
    }, [copy.drawingHint]);

    const changeVersion = useCallback((nextVersion) => {
        setVersion(nextVersion);
        setStrokes([]);
        setDrawing(false);
    }, []);

    const submitComment = useCallback(async () => {
        const commentVersion = annotationTarget === "original" ? "original" : (version || "original");
        if (!text.trim()) return;
        const input = {photoId: photo.id, authorName: authorName || copy.customer, version: commentVersion, text: text.trim(), strokes};
        try {
            const created = adminMode ? await adminPostRetouchComment(input) : await postRetouchComment(token, input);
            setComments((prev) => [...prev, created]);
            setText("");
            setStrokes([]);
            setEditing(false);
            setAnnotationTarget("retouch");
            message.success(copy.saveComment);
            onPhotoChanged?.({...photo, comments: [...comments, created], commentCount: comments.length + 1});
        } catch (error) {
            message.error(error.message || copy.loadFailed);
        }
    }, [adminMode, annotationTarget, authorName, comments, copy.customer, copy.loadFailed, copy.saveComment, onPhotoChanged, photo, strokes, text, token, version]);

    const approve = useCallback(async () => {
        if (!version) return;
        try {
            const result = adminMode
                ? await adminApproveRetouchPhoto({photoId: photo.id, version, authorName: authorName || copy.customer})
                : await approveRetouchPhoto(token, {
                    photoId: photo.id,
                    version,
                    authorName: authorName || copy.customer
                });
            const approval = result.approval || result;
            const createdComment = result.comment;
            const nextComments = createdComment ? [...comments, createdComment] : comments;
            setComments(nextComments);
            onPhotoChanged?.({...photo, approval, comments: nextComments, commentCount: nextComments.length});
            message.success(copy.approvedVersion.replace("{version}", formatRetouchVersion(version, copy)));
        } catch (error) {
            message.error(error.message || copy.loadFailed);
        }
    }, [adminMode, authorName, comments, copy, onPhotoChanged, photo, token, version]);

    const downloadCurrentVersion = useCallback(async () => {
        if (!version || downloading) return;
        if (isRetouchMobileDevice()) {
            if (!versionUrl) {
                message.error(copy.downloadFailed);
                return;
            }
            setPhotoPreviewOpen(false);
            setMobileSaveOpen(true);
            return;
        }
        setDownloading(true);
        const hide = message.loading(copy.downloading, 0);
        try {
            await downloadRetouchReviewPhoto({token, photoId: photo.id, version, relativePath: photo.relativePath});
            message.success(copy.downloaded);
        } catch (error) {
            message.error(error.message || copy.downloadFailed);
        } finally {
            hide();
            setDownloading(false);
        }
    }, [copy.downloadFailed, copy.downloaded, copy.downloading, downloading, photo.id, photo.relativePath, token, version, versionUrl]);

    const handleDownloadAllPhotos = useCallback(async () => {
        if (isRetouchMobileDevice()) {
            const items = photos.map((item) => {
                const latest = item.latestVersion || Object.keys(item.versions || {})[0] || "";
                const url = item.versions?.[latest]?.url || item.original?.url || null;
                return url ? {id: item.id, relativePath: item.relativePath, url} : null;
            }).filter(Boolean);
            if (!items.length) {
                message.error(copy.downloadFailed);
                return;
            }
            setBatchSavePhotos(items);
            setBatchSaveOpen(true);
            return;
        }
        if (!photos.length || zipDownloading) return;
        setZipDownloading(true);
        let hide = message.loading(`${copy.downloading}…`, 0);
        try {
            const result = await downloadAllRetouchPhotosAsZip({
                token,
                photos,
                zipName: albumName || copy.title,
                onProgress: ({current, total}) => {
                    hide();
                    hide = message.loading(copy.downloadAllProgress.replace("{current}", String(current)).replace("{total}", String(total)), 0);
                },
            });
            if (result.failed.length) {
                message.warning(copy.downloadAllPartial.replace("{succeeded}", String(result.succeeded)).replace("{failed}", String(result.failed.length)));
            } else {
                message.success(copy.downloadAllStarted);
            }
        } catch (error) {
            if (error.message === UNSUPPORTED_MESSAGE) message.warning(copy.downloadAllUnsupported);
            else if (error.message !== CANCELLED_MESSAGE) message.error(error.message || copy.downloadFailed);
        } finally {
            hide();
            setZipDownloading(false);
        }
    }, [albumName, copy, photos, token, zipDownloading]);

    const downloadMenuItems = useMemo(() => [
        {key: "current", icon: <DownloadOutlined/>, label: copy.download, onClick: downloadCurrentVersion, disabled: noRetouchVersion},
        {key: "all", icon: <DownloadOutlined/>, label: copy.downloadAll, onClick: handleDownloadAllPhotos},
    ], [copy.download, copy.downloadAll, downloadCurrentVersion, handleDownloadAllPhotos, noRetouchVersion]);

    const deleteComment = useCallback(async (commentId) => {
        if (!adminMode) return;
        try {
            await adminDeleteRetouchComment({photoId: photo.id, commentId});
            const nextComments = comments.filter((comment) => comment.id !== commentId);
            setComments(nextComments);
            onPhotoChanged?.({...photo, comments: nextComments, commentCount: nextComments.length});
        } catch (error) {
            message.error(error.message || copy.loadFailed);
        }
    }, [adminMode, comments, copy.loadFailed, onPhotoChanged, photo]);

    const navigateFromGallery = useCallback((nextPhoto) => {
        onNavigate?.(nextPhoto);
        setMobileGalleryOpen(false);
    }, [onNavigate]);

    const openAllPhotos = useCallback(() => {
        if (isRetouchMobileDevice()) {
            setMobileGalleryOpen(true);
            return;
        }
        galleryShellRef.current?.scrollIntoView({behavior: "smooth", block: "end"});
    }, []);

    const strokesToPath = (stroke) => stroke.map((point, index) => `${index ? "L" : "M"}${point.x} ${point.y}`).join(" ");
    const hasApprove = adminMode || role === "customer";
    const hasComment = adminMode || role === "customer" || role === "retoucher";
    const hasUpload = role === "retoucher" && typeof onUploadPhoto === "function";
    const hasBatchUpload = role === "retoucher" && typeof onUploadBatch === "function";
    const photoIndex = photos.findIndex((item) => item.id === photo.id);
    const photoCounter = photoIndex >= 0 ? `${photoIndex + 1}/${photos.length}` : "";
    const currentImageUrl = mobileOriginal ? imageState.originalUrl : imageState.versionUrl;
    const previewImageUrl = photoPreviewOriginal ? imageState.originalUrl : imageState.versionUrl;
    const mobileSaveUrl = imageState.versionUrl || versionUrl;
    const drawingOriginalStrokes = annotationTarget === "original";
    const retouchPaneComments = mobileOriginal ? originalComments : versionComments;
    const showDraftOnRetouchPane = mobileOriginal ? drawingOriginalStrokes : !drawingOriginalStrokes;
    const currentVersionApproved = photo.approval?.status === "approved" && photo.approval?.version === version;
    const approvalLabel = copy.approvedVersion.replace("{version}", formatRetouchVersion(version, copy));
    const isMobile = isRetouchMobileDevice();

    return (
        <>
            <section className="retouch-compare-shell" aria-label={photo.relativePath}>
                <div className="retouch-compare">
                    <div className="retouch-compare__photo-name" title={photo.relativePath}>
                        <span className="retouch-compare__photo-name-text"
                              title={photo.relativePath}>{photo.relativePath}</span>
                        {currentVersionApproved &&
                            <Tag color="success" icon={<CheckCircleFilled/>} role="status">{approvalLabel}</Tag>}
                    </div>
                    <div className="retouch-compare__toolbar">
                        <Button disabled={photoIndex <= 0}
                                onClick={() => onNavigate?.(photos[photoIndex - 1])}>{copy.previous}</Button>
                        <Button disabled={photoIndex < 0 || photoIndex >= photos.length - 1}
                                onClick={() => onNavigate?.(photos[photoIndex + 1])}>{copy.next}</Button>
                        {photoCounter && <span className="retouch-compare__photo-counter"
                                               aria-label={photoCounter}>{photoCounter}</span>}
                        <Button icon={<PictureOutlined/>} onClick={openAllPhotos}>{copy.allPhotos}</Button>
                        <Button icon={<ReloadOutlined/>} onClick={() => {
                            setScale(1);
                            setPan({x: 0, y: 0});
                        }}>{copy.resetZoom}</Button>
                        <Dropdown menu={{items: downloadMenuItems}}
                                  trigger={isMobile ? ["click"] : ["hover", "click"]}
                                  placement="bottomLeft">
                            <Button loading={downloading || zipDownloading}>
                                <Space>
                                    {copy.downloadLabel}
                                    <DownOutlined/>
                                </Space>
                            </Button>
                        </Dropdown>
                        {hasUpload && <Button icon={<UploadOutlined/>}
                                              onClick={() => onUploadPhoto(photo)}>{copy.uploadCurrent}</Button>}
                        {hasBatchUpload &&
                            <Button icon={<UploadOutlined/>} onClick={onUploadBatch}>{copy.upload}</Button>}
                        {hasComment && <Button type={editing ? "primary" : "default"} icon={<EditOutlined/>}
                                               onClick={toggleEditing}>{editing ? copy.view : copy.edit}</Button>}
                        {editing &&
                            <Button icon={<ClearOutlined/>} onClick={() => setStrokes([])}>{copy.clearDrawing}</Button>}
                        <Select value={version || undefined} onChange={changeVersion} options={versionOptions}
                                disabled={!versionOptions.length} placeholder={copy.noRetouchVersion}
                                aria-label={copy.version}/>
                    </div>
                    <div className="retouch-compare__stage">
                        <div className="retouch-compare__pane retouch-compare__pane--original">
                            <span className="retouch-compare__label">{copy.original}</span>
                            <div className="retouch-compare__viewport"
                                 onWheel={(event) => handleWheel(event, "original")} onGestureStart={handleGestureStart}
                                 onGestureChange={handleGestureChange} onGestureEnd={handleGestureEnd}
                                 onPointerDown={(event) => handlePointerDown(event, "original")}
                                 onPointerMove={(event) => {
                                     handlePointerMove(event);
                                     handlePanPointerMove(event);
                                 }} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp}>
                                <div className="retouch-compare__layer" style={transformStyle(scale, pan)}>
                                    <img key={`${photo.id}-original`} className="retouch-compare__image"
                                         src={imageState.photoId === photo.id && imageState.version === version ? imageState.originalUrl || undefined : undefined}
                                         alt="" draggable={false}/>
                                    <svg className="retouch-compare__drawing" viewBox="0 0 1 1"
                                         preserveAspectRatio="none">{originalComments.flatMap((comment) => (comment.strokes || []).map((stroke, index) =>
                                        <path key={`${comment.id}-${index}`} d={strokesToPath(stroke)} fill="none"
                                              stroke="#ff3b30" strokeWidth="0.006"/>))}{drawingOriginalStrokes && strokes.map((stroke, index) =>
                                        <path key={`draft-${index}`} d={strokesToPath(stroke)} fill="none"
                                              stroke="#00d4ff" strokeWidth="0.008"/>)}</svg>
                                </div>
                                {zoomHintTarget === "original" &&
                                    <div className="retouch-compare__zoom-hint-cover"><span>{copy.photoZoomHint}</span>
                                    </div>}
                            </div>
                            <ZoomControls scale={scale} copy={copy} onZoomIn={zoomIn} onZoomOut={zoomOut}/>
                        </div>
                        <div className="retouch-compare__pane retouch-compare__pane--retouch">
                            <div className="retouch-compare__version-control">
                                <Select className="retouch-compare__version-select" size="small" value={version || undefined}
                                        onChange={(nextVersion) => {
                                            changeVersion(nextVersion);
                                            setMobileOriginal(false);
                                            setPhotoPreviewOriginal(false);
                                        }} options={versionOptions} disabled={!versionOptions.length}
                                        placeholder={copy.noRetouchVersion} aria-label={copy.version}/>
                            </div>
                            <button type="button" className="retouch-compare__fullscreen-button" title={copy.fullscreen}
                                    aria-label={copy.fullscreen} onClick={openPhotoPreview}><FullscreenOutlined/>
                            </button>
                            <button type="button" className="retouch-compare__mobile-original-toggle"
                                    title={mobileOriginal ? copy.showRetouched : copy.showOriginal}
                                    onClick={() => {
                                        setMobileOriginal((value) => !value);
                                        setStrokes([]);
                                        setDrawing(false);
                                    }}>{mobileOriginal ? copy.showRetouched : copy.showOriginal}</button>
                            <div className="retouch-compare__viewport"
                                 onWheel={(event) => handleWheel(event, "retouch")} onGestureStart={handleGestureStart}
                                 onGestureChange={handleGestureChange} onGestureEnd={handleGestureEnd}
                                 onPointerDown={(event) => handlePointerDown(event, mobileOriginal ? "original" : "retouch")}
                                 onPointerMove={(event) => {
                                     handlePointerMove(event);
                                     handlePanPointerMove(event);
                                 }} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp}>
                                <div className="retouch-compare__layer" style={transformStyle(scale, pan)}>
                                    <img key={`${photo.id}-${version}-${mobileOriginal ? "original" : "retouch"}`}
                                         className="retouch-compare__image"
                                         src={imageState.photoId === photo.id && imageState.version === version ? currentImageUrl || undefined : undefined}
                                         alt="" draggable={false}/>
                                    <svg className="retouch-compare__drawing" viewBox="0 0 1 1"
                                         preserveAspectRatio="none">{retouchPaneComments.flatMap((comment) => (comment.strokes || []).map((stroke, index) =>
                                        <path key={`${comment.id}-${index}`} d={strokesToPath(stroke)} fill="none"
                                              stroke="#ff3b30" strokeWidth="0.006"/>))}{showDraftOnRetouchPane && strokes.map((stroke, index) =>
                                        <path key={`draft-${index}`} d={strokesToPath(stroke)} fill="none"
                                              stroke="#00d4ff" strokeWidth="0.008"/>)}</svg>
                                </div>
                                {zoomHintTarget === "retouch" &&
                                    <div className="retouch-compare__zoom-hint-cover"><span>{copy.photoZoomHint}</span>
                                    </div>}
                                {noRetouchVersion && !mobileOriginal &&
                                    <div className="retouch-compare__no-version-cover"><span>{copy.noRetouchVersion}</span>
                                    </div>}
                            </div>
                            <ZoomControls scale={scale} copy={copy} onZoomIn={zoomIn} onZoomOut={zoomOut}/>
                        </div>
                    </div>
                    <div className="retouch-compare__comment-panel">
                        <div>{editing && <p className="retouch-review-hint">{copy.drawingHint}</p>}
                            <div className="retouch-compare__comments">{loadingComments ?
                                <p>{copy.loading}</p> : comments.length ? comments.map((comment) => <div
                                    className="retouch-compare__comment" key={comment.id}>
                                    <div
                                        className="retouch-compare__comment-meta">{comment.authorName} · {comment.version === "original" && noRetouchVersion ? copy.noRetouchVersion : formatRetouchVersion(comment.version, copy)}{adminMode &&
                                        <Button type="link" danger size="small"
                                                onClick={() => deleteComment(comment.id)}>{copy.deleteComment}</Button>}</div>
                                    <div>{comment.type === "approval" ? copy.approvalComment.replace("{version}", formatRetouchVersion(comment.version, copy)) : comment.text}</div>
                                </div>) : <p>{copy.noComments}</p>}</div>
                        </div>
                        <div className="retouch-compare__note">
                            {hasComment &&
                                <Input.TextArea value={text} onChange={(event) => setText(event.target.value)}
                                                placeholder={copy.commentPlaceholder}
                                                autoSize={{minRows: 4, maxRows: 8}} maxLength={MAX_COMMENT_LENGTH}/>}
                            {hasComment && <Button type="primary" block disabled={!text.trim()} onClick={submitComment}
                                                   style={{marginTop: 8}}>{copy.send}</Button>}
                            {hasApprove &&
                                <Button icon={<CheckOutlined/>} type={currentVersionApproved ? "default" : "primary"}
                                        block disabled={currentVersionApproved || noRetouchVersion} onClick={approve}
                                        style={{marginTop: 8}}>{copy.approve}</Button>}
                        </div>
                    </div>
                </div>
            </section>
            <div ref={galleryShellRef}
                 className={`retouch-compare__gallery${mobileGalleryOpen ? " retouch-compare__gallery--open" : ""}`}>
                {!mobileGalleryOpen && <button type="button" className="retouch-compare__gallery-handle"
                                               onClick={() => setMobileGalleryOpen(true)} aria-label={copy.openGallery}>
                    <PictureOutlined/> <span>{copy.gallery}</span></button>}
                {mobileGalleryOpen && <button type="button" className="retouch-compare__gallery-backdrop"
                                              onClick={() => setMobileGalleryOpen(false)}
                                              aria-label={copy.closeGallery}/>}
                <div className="retouch-compare__gallery-panel">
                    <div className="retouch-compare__gallery-header">
                        <span>{copy.gallery}</span>
                        <button type="button" className="retouch-compare__gallery-close"
                                onClick={() => setMobileGalleryOpen(false)} aria-label={copy.closeGallery}>×
                        </button>
                    </div>
                    <RetouchPhotoGallery photos={photos} selectedId={photo.id} copy={copy}
                                         onSelect={navigateFromGallery}/>
                </div>
            </div>
            {photoPreviewOpen && (
                <div className="retouch-photo-preview" role="dialog" aria-modal="true" aria-label={photo.relativePath}>
                    <button type="button" className="retouch-photo-preview__backdrop" onClick={closePhotoPreview}
                            aria-label={copy.closePreview}/>
                    <div className="retouch-photo-preview__panel">
                        <div className="retouch-photo-preview__controls">
                            <Select className="retouch-photo-preview__version-select" size="small" value={version || undefined}
                                    onChange={(nextVersion) => {
                                        changeVersion(nextVersion);
                                        setPhotoPreviewOriginal(false);
                                    }} options={versionOptions} disabled={!versionOptions.length}
                                    placeholder={copy.noRetouchVersion} aria-label={copy.version}
                                    getPopupContainer={(trigger) => trigger.closest(".retouch-photo-preview__panel") || document.body}/>
                            <button type="button" className="retouch-photo-preview__close" onClick={closePhotoPreview}
                                    aria-label={copy.closePreview}>×
                            </button>
                        </div>
                        <div className="retouch-photo-preview__image-wrap">
                            {photoIndex > 0 && <button type="button"
                                                       className="retouch-photo-preview__nav retouch-photo-preview__nav--previous"
                                                       onClick={() => onNavigate?.(photos[photoIndex - 1])}
                                                       aria-label={copy.previous}><LeftOutlined/></button>}
                            <img key={`${photo.id}-${version}-${photoPreviewOriginal ? "original" : "retouch"}`}
                                 src={previewImageUrl || undefined} alt="" draggable={false}
                                 className="retouch-photo-preview__image"/>
                            {noRetouchVersion && !photoPreviewOriginal &&
                                <div className="retouch-photo-preview__no-version"><span>{copy.noRetouchVersion}</span>
                                </div>}
                            {photoIndex >= 0 && photoIndex < photos.length - 1 && <button type="button"
                                                                                          className="retouch-photo-preview__nav retouch-photo-preview__nav--next"
                                                                                          onClick={() => onNavigate?.(photos[photoIndex + 1])}
                                                                                          aria-label={copy.next}>
                                <RightOutlined/></button>}
                        </div>
                        <div className="retouch-photo-preview__actions">
                            <button type="button" className="retouch-photo-preview__toggle"
                                    onClick={() => setPhotoPreviewOriginal((value) => !value)}>{photoPreviewOriginal ? copy.showRetouched : copy.showOriginal}</button>
                            <button type="button" className="retouch-photo-preview__download"
                                    onClick={downloadCurrentVersion} disabled={downloading}>{copy.download}</button>
                        </div>
                        <p className="retouch-photo-preview__name">{photo.relativePath}</p>
                    </div>
                </div>
            )}
            {mobileSaveOpen && (
                <div className="retouch-mobile-save" role="dialog" aria-modal="true" aria-label={copy.mobileSaveTitle}>
                    <button type="button" className="retouch-mobile-save__backdrop"
                            onClick={() => setMobileSaveOpen(false)} aria-label={copy.closePreview}/>
                    <div className="retouch-mobile-save__panel">
                        <header className="retouch-mobile-save__header">
                            <div>
                                <h2>{copy.mobileSaveTitle}</h2>
                                <p>{copy.mobileSaveHint}</p>
                            </div>
                            <button type="button" className="retouch-mobile-save__close"
                                    onClick={() => setMobileSaveOpen(false)} aria-label={copy.closePreview}>×
                            </button>
                        </header>
                        <div className="retouch-mobile-save__image-wrap">
                            <img src={mobileSaveUrl || undefined} alt="" className="retouch-mobile-save__image"
                                 draggable={false}/>
                        </div>
                        <p className="retouch-mobile-save__name">{photo.relativePath}</p>
                    </div>
                </div>
            )}
            <RetouchBatchSavePanel photos={batchSavePhotos} open={batchSaveOpen}
                                   onClose={() => setBatchSaveOpen(false)} copy={copy}/>
        </>
    );
}

const MAX_COMMENT_LENGTH = 4000;
export default RetouchCompare;
